import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { CalendarHeaderComponent } from 'src/app/shared/components/calendar-header/calendar-header.component';
import {
  DIARY_RANGE_ALL,
  DIARY_STATUS_OPTIONS,
  resolveDiaryDateWindow,
  resolveDiaryStatuses,
} from '../daily-diary-filters.util';

/**
 * Chip-style filters for the Daily diary tab, mirroring the daily-report lists
 * (`app-project-details-page-status-site-filter` + `-date-filter`): a
 * "Filter (N)" pill with the sliders icon and a date pill with the calendar
 * icon, both rendered on the tab row.
 *
 * State lives in the query params so the list can sit inside the tab body while
 * the chips render above it; the defaults come from `daily-diary-filters.util`
 * so the chips and the list can never disagree.
 */
@Component({
  selector: 'app-daily-diary-filters',
  templateUrl: './daily-diary-filters.component.html',
  styleUrls: [
    '../../../../projects-details-page/components/filters/styles.scss',
    './daily-diary-filters.component.scss',
  ],
  standalone: false,
})
export class DailyDiaryFiltersComponent implements OnInit, OnDestroy {
  // Shared Figma-styled calendar header (chevrons + white-chip tooltip).
  public calendarHeader = CalendarHeaderComponent;

  isOpen = false;
  buttonLabel = 'Filter (0)';

  // All statuses are selected by default: the list shows drafts AND submitted
  // diaries on first load, and nothing is excluded.
  statusOptions = DIARY_STATUS_OPTIONS.map(option => ({ ...option, isChecked: true }));

  /** Chip text for the active window — never claims "All Time" for a bounded range. */
  dateLabel = 'All Time';
  /**
   * Shown whenever a bounded window is in effect, default or not — the daily
   * report's date chip does the same (it renders the X whenever startDate or
   * endDate is set). Hiding it on the default left the chip with no way to
   * clear the filter.
   */
  showClearDate = false;

  searchForm = new FormGroup({
    statusSearch: new FormControl(''),
  });

  form = new FormGroup({
    startDate: new FormControl<any>(null),
    endDate: new FormControl<any>(null),
  });

  /**
   * True while we are writing the query-param state back into the range input.
   * `mat-date-range-input` pushes its own corrections through the control's
   * value accessor (which always emits, `emitEvent: false` notwithstanding), so
   * without this guard pushing a new window in could bounce a half-updated
   * start/end pair straight back out into the URL.
   */
  private syncing = false;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const activeStatuses = resolveDiaryStatuses(qp);
        this.statusOptions.forEach(option => {
          // Empty = "every status", so every box is ticked.
          option.isChecked = !activeStatuses.length || activeStatuses.includes(option.value);
        });
        this.buttonLabel = `Filter (${activeStatuses.length ? 1 : 0})`;

        const window = resolveDiaryDateWindow(qp);
        this.dateLabel = this.buildDateLabel(window);
        this.showClearDate = !window.isAllTime;
        this.syncDateControls(window.startDate, window.endDate);
      });

    // Only the end date navigates — a range picker emits the start first and
    // navigating then would drop the half-finished selection.
    this.form.controls.endDate.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (this.syncing) return;

        const start = this.form.controls.startDate.value;
        const end = this.form.controls.endDate.value;
        if (!start || !end || !moment(start).isValid() || !moment(end).isValid()) return;
        // Half-finished / inverted selections are not a filter.
        if (moment(start).isAfter(moment(end), 'day')) return;

        const diaryStart = moment(start).format('YYYY-MM-DD');
        const diaryEnd = moment(end).format('YYYY-MM-DD');
        const currentQP = this.route.snapshot.queryParams;
        if (currentQP['diaryStart'] === diaryStart
          && currentQP['diaryEnd'] === diaryEnd
          && currentQP['diaryRange'] !== DIARY_RANGE_ALL) return;

        this.router.navigate([], {
          queryParams: { diaryStart, diaryEnd, diaryRange: null },
          queryParamsHandling: 'merge',
        });
      });
  }

  onStatusCheckboxChange(selected: string[]): void {
    // Nothing selected, or everything selected, is the default — drop the param
    // so a cleared filter can never render an empty list.
    const isDefault = !selected?.length || selected.length === this.statusOptions.length;
    this.router.navigate([], {
      queryParams: { diaryStatus: isDefault ? null : selected },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Removes the date filter, the way the report's chip does — an X on a filter
   * chip means "drop this filter", so it goes to All Time rather than back to
   * the two-week default. The default still applies on a fresh load.
   */
  resetDateFilter(event: Event): void {
    event.stopPropagation();
    this.router.navigate([], {
      queryParams: {
        diaryStart: null,
        diaryEnd: null,
        diaryRange: DIARY_RANGE_ALL,
      },
      queryParamsHandling: 'merge',
    });
  }

  private buildDateLabel(window: { startDate: string; endDate: string; isAllTime: boolean }): string {
    if (window.isAllTime) return 'All Time';
    if (window.startDate === window.endDate) return moment(window.startDate).format('D, MMM');
    return `${moment(window.startDate).format('D, MMM')} - ${moment(window.endDate).format('D, MMM')}`;
  }

  private syncDateControls(startDate: string, endDate: string): void {
    const nextStart = startDate ? moment(startDate, 'YYYY-MM-DD') : null;
    const nextEnd = endDate ? moment(endDate, 'YYYY-MM-DD') : null;

    if (this.isSameDay(this.form.controls.startDate.value, nextStart)
      && this.isSameDay(this.form.controls.endDate.value, nextEnd)) return;

    this.syncing = true;
    // Blank both before writing: setting a start later than the current end
    // puts the range input in an invalid state it tries to "fix" on its own.
    this.form.controls.startDate.setValue(null, { emitEvent: false });
    this.form.controls.endDate.setValue(null, { emitEvent: false });
    this.form.controls.startDate.setValue(nextStart, { emitEvent: false });
    this.form.controls.endDate.setValue(nextEnd, { emitEvent: false });
    this.syncing = false;
  }

  private isSameDay(a: any, b: any): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return moment(a).isSame(moment(b), 'day');
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
